import {
  useCallback,
  useEffect,
  useRef,
  useState } from 'react';
import {
  List,
  ListItem,
  ListItemText,
  Typography,
  Container,
  Paper,
  TextField,
  IconButton,
  Box,
  ListItemAvatar,
  Avatar,
  Button,
  CircularProgress,
  Tooltip,
  FormControl,
  InputLabel,
  MenuItem,
  Checkbox,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Chip
} from '@mui/material';
import Select from '../components/SearchableSelect';
import ModalProdotto from './ModalProdotto';
import { useTranslation } from 'react-i18next';
import SidebarLayout from '../components/sidebarLayout';
import { aggiornaProdotto, getAllProdotti, eliminaProdotto, eliminaProdotti, importaProdottiExcel, bulkAssignProductPrinters } from '../APIs/ProductsAPI';
import TimePicker24Field from "../components/TimePicker24Field";
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import SortByAlphaIcon from '@mui/icons-material/SortByAlpha';
import PriceChangeOutlinedIcon from '@mui/icons-material/PriceChangeOutlined';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import PrintIcon from '@mui/icons-material/Print';
import { SetupWSEventListener } from '../utils/setupWSEventListener';
import { Search } from '@mui/icons-material';
import ModalVariantList from '../components/ModalVariantList';
import { useSnackbar } from '../contexts/SnackBarContext';
import { getTenantTranslation } from '../utils/getTenantTranslation';
import { getAllCategorie } from '../APIs/CategoriesAPI';
import { getAllSottocategorie } from '../APIs/SubCategoriesAPI';
import { getAllStanze } from '../APIs/RoomsAPI';
import { getConfiguredPrinters } from '../APIs/PrintersAPI';
import { formatBoardBasis } from '../utils/boardBasis';
import _ from 'lodash';

function normalizeSubcategoryEntries(subcategories = [], categories = []) {
  const byId = new Map();
  const addSubcategory = (subcategory, fallbackCategoryId = null) => {
    const id = Number(subcategory?.id);
    if (!Number.isInteger(id) || id <= 0) return;
    const categoryId = Number(
      subcategory?.categoryId ??
      subcategory?.category?.id ??
      fallbackCategoryId
    );
    byId.set(id, {
      ...subcategory,
      id,
      categoryId: Number.isInteger(categoryId) && categoryId > 0 ? categoryId : null,
    });
  };

  (subcategories || []).forEach((subcategory) => addSubcategory(subcategory));
  (categories || []).forEach((category) => {
    (category?.subcategories || []).forEach((subcategory) => addSubcategory(subcategory, category.id));
  });

  return Array.from(byId.values());
}

const DEFAULT_TIME_SLOT = () => ({
  from: parseTimeValue('00:00'),
  to: parseTimeValue('23:59'),
});

function parseTimeValue(timeStr) {
  if (!timeStr) return null;
  const [hours, minutes] = timeStr.split(':').map(Number);
  if (!Number.isInteger(hours) || !Number.isInteger(minutes)) return null;
  const date = new Date();
  date.setHours(hours, minutes, 0, 0);
  return date;
}

function normalizeProduct(product) {
  const firstCat = product.categories?.[0];
  return {
    ...product,
    categoryId: firstCat?.categoryId ?? null,
    subcategoryId: firstCat?.subcategoryId ?? null,
    categoryName: firstCat?.category?.name ?? null,
    subcategoryName: firstCat?.subcategory?.name ?? null,
  };
}

function normalizePrinterNames(printers) {
  return (Array.isArray(printers) ? printers : [])
    .map((printer) => (typeof printer === 'string' ? printer : printer?.name))
    .map((name) => String(name || '').trim())
    .filter(Boolean);
}

function ListaProdotti() {
  const [products, setProducts] = useState([]);
  const { t } = useTranslation();
  const tenantType = localStorage.getItem('tenantType');
  const isRestaurantTenant = String(tenantType || '').toUpperCase() === 'RISTORANTE';
  const { showSnackbar } = useSnackbar();

  const [openModal, setOpenModal] = useState(false);
  const [modalMode, setModalMode] = useState('create');
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [openVariantList, setOpenVariantList] = useState();
  const [prodottoSelezionato, setProdottoSelezionato] = useState(false);

  const [cercaProdotto, setCercaProdotto] = useState('');
  const [prodottiFiltrati, setProdottiFiltrati] = useState(products);
  const [categorie, setCategorie] = useState([]);
  const [sottocategorie, setSottocategorie] = useState([]);
  const [rooms, setRooms] = useState([]);
  const [selectedCategoryFilter, setSelectedCategoryFilter] = useState('');
  const [selectedSubcategoryFilter, setSelectedSubcategoryFilter] = useState('');
  const [ascFiltroName, setAscFiltroName] = useState(false);
  const [ascFiltroPrice, setAscFiltroPrice] = useState(false);
  const [importingProductsExcel, setImportingProductsExcel] = useState(false);
  const importProductsInputRef = useRef(null);
  const [selectedProductIds, setSelectedProductIds] = useState(new Set());
  const [bulkPrinterDialogOpen, setBulkPrinterDialogOpen] = useState(false);
  const [configuredPrinters, setConfiguredPrinters] = useState([]);
  const [bulkPrinterTargets, setBulkPrinterTargets] = useState([]);
  const [bulkAssigningPrinters, setBulkAssigningPrinters] = useState(false);
  const [bulkDeleteDialogOpen, setBulkDeleteDialogOpen] = useState(false);
  const [bulkDeletingProducts, setBulkDeletingProducts] = useState(false);

  const getImageSrc = (photoPath) => {
    if (!photoPath) return null;
    if (/^https?:\/\//i.test(photoPath)) return photoPath;
    return photoPath.startsWith('/') ? photoPath : `/${photoPath}`;
  };

  const productHasCategory = useCallback((product, categoryId) => {
    if (!categoryId) return true;
    const normalizedCategoryId = Number(categoryId);
    if (Array.isArray(product.categories) && product.categories.length > 0) {
      return product.categories.some((entry) =>
        Number(entry?.categoryId ?? entry?.category?.id) === normalizedCategoryId
      );
    }
    return Number(product.categoryId) === normalizedCategoryId;
  }, []);

  const productHasSubcategory = useCallback((product, subcategoryId) => {
    if (!subcategoryId) return true;
    const normalizedSubcategoryId = Number(subcategoryId);
    if (Array.isArray(product.categories) && product.categories.length > 0) {
      return product.categories.some((entry) =>
        Number(entry?.subcategoryId ?? entry?.subcategory?.id) === normalizedSubcategoryId
      );
    }
    return Number(product.subcategoryId) === normalizedSubcategoryId;
  }, []);

  const fetchProdotti = useCallback(() => {
    const token = localStorage.getItem('accessToken');
    const tenant = localStorage.getItem('tenantId');

    return getAllProdotti(token, tenant, { includeInactive: true })
      .then((data) => {
        const dataWithSlots = data.map(product => {
          const normalized = normalizeProduct(product);
          const timeSlots = (product.visibility || []).map(slot => ({
            from: parseTime(slot.startTime),
            to: parseTime(slot.endTime)
          }));
          if (timeSlots.length === 0) timeSlots.push(DEFAULT_TIME_SLOT());
          return { ...normalized, timeSlots };
        });
        setProducts(_.sortBy(dataWithSlots, ['categoryId', 'subcategoryId'], ['asc', 'asc']));
      })
      .catch((error) => console.error('Errore nel caricamento prodotti:', error.message));
  }, []);

  const fetchFilters = useCallback(async () => {
    const token = localStorage.getItem('accessToken');
    const tenant = localStorage.getItem('tenantId');
    const isRestaurantTenant = String(localStorage.getItem('tenantType') || '').toUpperCase() === 'RISTORANTE';
    const [categorieResult, sottocategorieResult, roomsResult, printersResult] = await Promise.allSettled([
      getAllCategorie(token, tenant, { includeInactive: true }),
      getAllSottocategorie(token, tenant),
      isRestaurantTenant ? getAllStanze(token, tenant) : Promise.resolve([]),
      isRestaurantTenant ? getConfiguredPrinters(token, tenant) : Promise.resolve([]),
    ]);

    const categorieData = categorieResult.status === 'fulfilled' && Array.isArray(categorieResult.value)
      ? categorieResult.value
      : [];
    const sottocategorieData = sottocategorieResult.status === 'fulfilled' && Array.isArray(sottocategorieResult.value)
      ? sottocategorieResult.value
      : [];
    const roomsData = roomsResult.status === 'fulfilled' && Array.isArray(roomsResult.value)
      ? roomsResult.value
      : [];
    const printersData = printersResult.status === 'fulfilled' && Array.isArray(printersResult.value)
      ? printersResult.value
      : [];

    if (categorieResult.status === 'rejected') console.error('Errore nel caricamento categorie prodotti:', categorieResult.reason?.message || categorieResult.reason);
    if (sottocategorieResult.status === 'rejected') console.error('Errore nel caricamento sottocategorie prodotti:', sottocategorieResult.reason?.message || sottocategorieResult.reason);
    if (roomsResult.status === 'rejected') console.error('Errore nel caricamento sale prodotto:', roomsResult.reason?.message || roomsResult.reason);
    if (printersResult.status === 'rejected') console.error('Errore nel caricamento stampanti prodotto:', printersResult.reason?.message || printersResult.reason);

    setCategorie(_.sortBy(categorieData, ['name']));
    setSottocategorie(_.sortBy(normalizeSubcategoryEntries(sottocategorieData, categorieData), ['name']));
    setRooms(roomsData);
    setConfiguredPrinters(normalizePrinterNames(printersData));
  }, []);

  useEffect(() => {

    fetchProdotti();
    fetchFilters();

    const cleanup = SetupWSEventListener(fetchProdotti);

    const handleProdotto = () => fetchProdotti();
    window.addEventListener('prodottoCreato', handleProdotto);
    window.addEventListener('prodottoAggiornato', handleProdotto);
    window.addEventListener('prodottoEliminato', handleProdotto);

    return () => {
      cleanup();
      window.removeEventListener('prodottoCreato', handleProdotto);
      window.removeEventListener('prodottoAggiornato', handleProdotto);
      window.removeEventListener('prodottoEliminato', handleProdotto);
    };
  }, [fetchFilters, fetchProdotti]);

  useEffect(() => {
    const search = cercaProdotto.trim().toLowerCase();
    const filtered = products.filter(p =>
      p.name.toLowerCase().includes(search) &&
      productHasCategory(p, selectedCategoryFilter) &&
      productHasSubcategory(p, selectedSubcategoryFilter)
    );
    setProdottiFiltrati(filtered);
  }, [cercaProdotto, products, selectedCategoryFilter, selectedSubcategoryFilter, productHasCategory, productHasSubcategory]);

  useEffect(() => {
    setSelectedSubcategoryFilter('');
  }, [selectedCategoryFilter]);

  const visibleSubcategories = selectedCategoryFilter
    ? sottocategorie.filter((sub) => Number(sub.categoryId ?? sub.category?.id) === Number(selectedCategoryFilter))
    : sottocategorie;
  const visibleProductIds = prodottiFiltrati
    .map((product) => Number(product.id))
    .filter((id) => Number.isInteger(id) && id > 0);
  const hasSelectedProducts = selectedProductIds.size > 0;
  const allVisibleProductsSelected = visibleProductIds.length > 0
    && visibleProductIds.every((id) => selectedProductIds.has(id));
  const someVisibleProductsSelected = visibleProductIds.some((id) => selectedProductIds.has(id));

  function parseTime(timeStr) {
    return parseTimeValue(timeStr);
  }

  const handleApriNuovo = () => {
    setModalMode('create');
    setSelectedProduct(null);
    setOpenModal(true);
  };

  const handleApriModifica = (prodotto) => {
    setModalMode('edit');
    setSelectedProduct(prodotto); // already normalized, has categoryId flat
    setOpenModal(true);
  };

  const inviaFasciaOraria = async (productId, timeSlots) => {
    const token = localStorage.getItem('accessToken');
    const tenant = localStorage.getItem('tenantId');

    const prodotto = products.find(p => p.id === productId);
    if (!prodotto) return;

    const visibility = timeSlots
      .filter(slot => slot.from && slot.to)
      .map(slot => ({
        startTime: slot.from.toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit', hour12: false }),
        endTime: slot.to.toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit', hour12: false })
      }));

    const updatedProduct = {
      name: prodotto.name,
      price: parseFloat(prodotto.price),
      pricePerWeight: prodotto.pricePerWeight,
      typeOfWeight: prodotto.typeOfWeight ?? null,
      categoryId: prodotto.categoryId,
      subcategoryId: prodotto.subcategoryId,
      visibility,
      photoPath: prodotto.photoPath ?? null,
    };

    try {
      await aggiornaProdotto(productId, updatedProduct, token, tenant);
      handleAlert(t('productList_alert.success_dateslot') + productId, 'success');
    } catch (error) {
      handleAlert(t('productList_alert.error_dateslot') + error.message, 'error');
    }
  };

  const handleElimina = async (productId) => {
    const token = localStorage.getItem('accessToken');
    const tenant = localStorage.getItem('tenantId');
    try {
      await eliminaProdotto(productId, token, tenant);
      window.dispatchEvent(new CustomEvent('prodottoEliminato', { detail: { id: productId } }));
    } catch (error) {
      handleAlert(t('reservations_details.error') + error.message);
    }
  };

  const buildDuplicateName = (originalName) => {
    const trimmedName = (originalName || '').trim();
    const baseName = trimmedName || t('new_product');
    const normalizedNames = new Set(products.map((p) => (p.name || '').trim().toLowerCase()));

    let counter = 1;
    while (true) {
      const suffix = counter === 1 ? 'Copia' : `Copia ${counter}`;
      const candidate = `${baseName} (${suffix})`;
      if (!normalizedNames.has(candidate.toLowerCase())) return candidate;
      counter += 1;
    }
  };

  const handleApriDuplica = (prodotto) => {
    setModalMode('duplicate');
    setSelectedProduct({ ...prodotto, name: buildDuplicateName(prodotto.name) });
    setOpenModal(true);
  };

  const handleTimeChange = (productId, slotIndex, field, newValue) => {
    setProducts(prev =>
      prev.map(prod => {
        if (prod.id !== productId) return prod;
        const newSlots = [...prod.timeSlots];
        newSlots[slotIndex] = { ...newSlots[slotIndex], [field]: newValue };
        const tuttiCompleti = newSlots.every(s => s.from && s.to);
        if (tuttiCompleti) inviaFasciaOraria(productId, newSlots);
        return { ...prod, timeSlots: newSlots };
      })
    );
  };

  const addTimeSlot = (productId) => {
    setProducts(prev =>
      prev.map(prod => {
        if (prod.id !== productId) return prod;
        return { ...prod, timeSlots: [...prod.timeSlots, DEFAULT_TIME_SLOT()] };
      })
    );
  };

  const removeTimeSlot = (productId, slotIndex) => {
    setProducts(prev =>
      prev.map(prod => {
        if (prod.id !== productId) return prod;
        const newSlots = prod.timeSlots.filter((_, i) => i !== slotIndex);
        return { ...prod, timeSlots: newSlots.length ? newSlots : [DEFAULT_TIME_SLOT()] };
      })
    );
  };

  const handleSearch = (e) => setCercaProdotto(e.target.value);

  const handleImportProductsExcel = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;

    if (!/\.(xlsx|xls)$/i.test(file.name)) {
      handleAlert('Seleziona un file Excel .xlsx o .xls', 'warning');
      return;
    }

    const token = localStorage.getItem('accessToken');
    const tenant = localStorage.getItem('tenantId');
    setImportingProductsExcel(true);

    try {
      const result = await importaProdottiExcel(file, token, tenant);
      await Promise.all([fetchProdotti(), fetchFilters()]);
      handleAlert(
        `Import Excel completato: ${result.imported || 0} prodotti importati, ${result.skipped || 0} righe scartate`,
        (result.skipped || 0) > 0 ? 'warning' : 'success'
      );
    } catch (error) {
      handleAlert(error?.message || 'Errore durante import Excel prodotti', 'error');
    } finally {
      setImportingProductsExcel(false);
    }
  };

  const toggleProductSelection = (productId) => {
    const normalizedId = Number(productId);
    if (!Number.isInteger(normalizedId) || normalizedId <= 0) return;
    setSelectedProductIds((prev) => {
      const next = new Set(prev);
      if (next.has(normalizedId)) next.delete(normalizedId);
      else next.add(normalizedId);
      return next;
    });
  };

  const toggleVisibleProductSelection = () => {
    setSelectedProductIds((prev) => {
      const next = new Set(prev);
      if (allVisibleProductsSelected) {
        visibleProductIds.forEach((id) => next.delete(id));
      } else {
        visibleProductIds.forEach((id) => next.add(id));
      }
      return next;
    });
  };

  const handleOpenBulkPrinterDialog = () => {
    if (!hasSelectedProducts) {
      handleAlert(t('bulkPrinters.selectProductFirst'), 'warning');
      return;
    }
    setBulkPrinterTargets([]);
    setBulkPrinterDialogOpen(true);
  };

  const handleBulkAssignPrinters = async () => {
    if (!hasSelectedProducts) {
      handleAlert(t('bulkPrinters.selectProductFirst'), 'warning');
      return;
    }
    if (bulkPrinterTargets.length === 0) {
      handleAlert(t('bulkPrinters.selectPrinterFirst'), 'warning');
      return;
    }

    const token = localStorage.getItem('accessToken');
    const tenant = localStorage.getItem('tenantId');
    setBulkAssigningPrinters(true);

    try {
      const productIds = Array.from(selectedProductIds);
      const result = await bulkAssignProductPrinters(productIds, bulkPrinterTargets, token, tenant);
      await fetchProdotti();
      setSelectedProductIds(new Set());
      setBulkPrinterDialogOpen(false);
      setBulkPrinterTargets([]);
      handleAlert(t('bulkPrinters.updatedSuccess', { count: result.updated || productIds.length }), 'success');
    } catch (error) {
      handleAlert(error?.message || t('bulkPrinters.assignError'), 'error');
    } finally {
      setBulkAssigningPrinters(false);
    }
  };

  const handleBulkDeleteProducts = async () => {
    if (!hasSelectedProducts) return;

    const token = localStorage.getItem('accessToken');
    const tenant = localStorage.getItem('tenantId');
    const productIds = Array.from(selectedProductIds);
    setBulkDeletingProducts(true);

    try {
      const result = await eliminaProdotti(productIds, token, tenant);
      setSelectedProductIds(new Set());
      setBulkDeleteDialogOpen(false);
      await fetchProdotti();
      window.dispatchEvent(new CustomEvent('prodottoEliminato', { detail: { ids: productIds } }));
      handleAlert(t('bulkDelete.productsDeleted', { count: result.deletedCount || productIds.length }), 'success');
    } catch (error) {
      handleAlert(error?.message || t('bulkDelete.deleteProductsError'), 'error');
    } finally {
      setBulkDeletingProducts(false);
    }
  };

  function handleSort(str) {
    const sorted = [...prodottiFiltrati];
    if (str === 'name') {
      if (ascFiltroName) sorted.sort((a, b) => (a.name < b.name) ? 1 : ((b.name < a.name) ? -1 : 0));
      else sorted.sort((a, b) => (a.name > b.name) ? 1 : ((b.name > a.name) ? -1 : 0));
      setAscFiltroPrice(false);
    } else if (str === 'price') {
      if (ascFiltroPrice) sorted.sort((a, b) => (a.price < b.price) ? 1 : ((b.price < a.price) ? -1 : 0));
      else sorted.sort((a, b) => (a.price > b.price) ? 1 : ((b.price > a.price) ? -1 : 0));
      setAscFiltroName(false);
    }
    setProdottiFiltrati(sorted);
  }

  function handleWeight(unit) {
    if (unit === 'KILOGRAM') return 'Kg';
    else if (unit === 'HECTOGRAM') return 'hg';
    else return '';
  }

  function getProductCategoryLabels(product) {
    const labels = Array.isArray(product?.categories)
      ? product.categories
        .map((entry) => {
          const categoryName = entry?.category?.name;
          const subcategoryName = entry?.subcategory?.name;
          if (categoryName && subcategoryName) return `${categoryName} - ${subcategoryName}`;
          return categoryName || subcategoryName || null;
        })
        .filter(Boolean)
      : [];

    if (labels.length > 0) return [...new Set(labels)];

    if (product?.categoryName && product?.subcategoryName) return [`${product.categoryName} - ${product.subcategoryName}`];
    if (product?.categoryName) return [product.categoryName];

    return [];
  }

  function getProductRoomLabel(product) {
    if (!isRestaurantTenant) return null;
    const productRoomIds = Array.isArray(product?.roomIds)
      ? product.roomIds.map(Number).filter((id) => Number.isInteger(id) && id > 0)
      : [];
    if (productRoomIds.length === 0) return t('product_room_all');

    return productRoomIds
      .map((roomId) => rooms.find((room) => Number(room.id) === roomId)?.name || `#${roomId}`)
      .join(', ');
  }

  function handleAlert(msg, type) {
    showSnackbar(msg, type);
  }

  return (
    <SidebarLayout className="admin-responsive-page">
      <Container sx={{ mt: { xs: 2, md: 4 } }}>
        <Box
          justifyContent="space-between"
          alignItems={{ xs: 'stretch', md: 'center' }}
          display="flex"
          gap={2}
          flexWrap="wrap"
          mb={2}
        >
          <Box
            display="flex"
            gap={{ xs: 1.25, sm: 2 }}
            flexWrap="wrap"
            alignItems="center"
            sx={{ flex: '1 1 0', minWidth: 0, width: { xs: '100%', md: 'auto' } }}
          >
            <Typography
              variant="h5"
              gutterBottom={false}
              sx={{ flexShrink: 0, width: { xs: '100%', sm: 'auto' } }}
            >
              {getTenantTranslation(t, tenantType, 'products')}
            </Typography>
            <TextField
              value={cercaProdotto}
              onChange={handleSearch}
              placeholder={t('search')}
              size="small"
              variant="outlined"
              InputProps={{ startAdornment: <Search sx={{ mr: 1 }} /> }}
              sx={{
                flex: { xs: '1 1 100%', sm: '1 1 240px' },
                minWidth: 0,
                maxWidth: { xs: '100%', md: 340 },
              }}
            />
            <FormControl
              size="small"
              sx={{ flex: { xs: '1 1 100%', sm: '1 1 220px' }, minWidth: 0, maxWidth: { xs: '100%', md: 260 } }}
            >
              <InputLabel id="product-category-filter-label">{t('category')}</InputLabel>
              <Select
                labelId="product-category-filter-label"
                value={selectedCategoryFilter}
                label={t('category')}
                onChange={(event) => setSelectedCategoryFilter(event.target.value)}
              >
                <MenuItem value="">{t('all')}</MenuItem>
                {categorie.map((cat) => (
                  <MenuItem key={cat.id} value={cat.id}>{cat.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl
              size="small"
              sx={{ flex: { xs: '1 1 100%', sm: '1 1 220px' }, minWidth: 0, maxWidth: { xs: '100%', md: 260 } }}
            >
              <InputLabel id="product-subcategory-filter-label">{t('subcategory')}</InputLabel>
              <Select
                labelId="product-subcategory-filter-label"
                value={selectedSubcategoryFilter}
                label={t('subcategory')}
                onChange={(event) => setSelectedSubcategoryFilter(event.target.value)}
              >
                <MenuItem value="">{t('all')}</MenuItem>
                {visibleSubcategories.map((sub) => (
                  <MenuItem key={sub.id} value={sub.id}>{sub.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
            {(cercaProdotto || selectedCategoryFilter || selectedSubcategoryFilter) && (
              <Button
                variant="outlined"
                onClick={() => {
                  setCercaProdotto('');
                  setSelectedCategoryFilter('');
                  setSelectedSubcategoryFilter('');
                }}
                sx={{ flexShrink: 0 }}
              >
                {t('common.actions.clearFilter')}
              </Button>
            )}
            <Tooltip title={t('sortName')}>
              <Button
                onClick={() => { handleSort('name'); setAscFiltroName(!ascFiltroName); }}
                sx={{ minWidth: 40, px: 1 }}
              >
                <SortByAlphaIcon />
              </Button>
            </Tooltip>
            <Tooltip title={t('sortPrice')}>
              <Button
                onClick={() => { handleSort('price'); setAscFiltroPrice(!ascFiltroPrice); }}
                sx={{ minWidth: 40, px: 1 }}
              >
                <PriceChangeOutlinedIcon />
              </Button>
            </Tooltip>
          </Box>
          <Button
            variant="contained"
            onClick={handleApriNuovo}
            sx={{ width: { xs: '100%', sm: 'auto' }, flexShrink: 0 }}
          >
            {getTenantTranslation(t, tenantType, 'add_product')}
          </Button>
          <input
            ref={importProductsInputRef}
            hidden
            type="file"
            accept=".xlsx,.xls,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel"
            onChange={handleImportProductsExcel}
          />
          <Button
            variant="outlined"
            startIcon={importingProductsExcel ? <CircularProgress size={16} /> : <UploadFileIcon />}
            onClick={() => importProductsInputRef.current?.click()}
            disabled={importingProductsExcel}
            sx={{ width: { xs: '100%', sm: 'auto' }, flexShrink: 0 }}
          >
            {importingProductsExcel ? 'Importazione...' : 'Importa Excel'}
          </Button>
        </Box>

        <Box
            sx={{
              mb: 2,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 1,
              flexWrap: 'wrap',
            }}
          >
            <Button
              variant="outlined"
              onClick={toggleVisibleProductSelection}
              disabled={visibleProductIds.length === 0}
              startIcon={<Checkbox checked={allVisibleProductsSelected} indeterminate={!allVisibleProductsSelected && someVisibleProductsSelected} />}
            >
              {allVisibleProductsSelected ? t('bulkPrinters.deselectVisible') : t('bulkPrinters.selectVisible')}
            </Button>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
              <Typography variant="body2" color="text.secondary">
                {t('bulkPrinters.selectedCount', { count: selectedProductIds.size })}
              </Typography>
              {isRestaurantTenant && (
                <Button
                  variant="contained"
                  startIcon={<PrintIcon />}
                  onClick={handleOpenBulkPrinterDialog}
                  disabled={!hasSelectedProducts}
                >
                  {t('bulkPrinters.assignPrinters')}
                </Button>
              )}
              <Button
                variant="outlined"
                color="error"
                startIcon={<DeleteIcon />}
                onClick={() => setBulkDeleteDialogOpen(true)}
                disabled={!hasSelectedProducts}
              >
                {t('bulkDelete.deleteSelected')}
              </Button>
            </Box>
          </Box>

        <Paper elevation={2} sx={{ overflow: 'hidden' }}>
          {prodottiFiltrati.length === 0 ? (
            <Typography sx={{ p: 2 }}>{t('no_products')}</Typography>
          ) : (
            <List disablePadding>
              {prodottiFiltrati.slice().map((product) => {
                const imgSrc = getImageSrc(product.photoPath);
                const productRoomLabel = getProductRoomLabel(product);
                return (
                  <ListItem
                    key={product.id}
                    divider
                    sx={{
                      flexDirection: 'column',
                      alignItems: 'flex-start',
                      gap: 1,
                      px: { xs: 1.5, sm: 2 },
                      py: { xs: 1.5, sm: 2 },
                    }}
                  >
                    <Box
                      sx={{
                        width: '100%',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: { xs: 'stretch', sm: 'flex-start' },
                        flexDirection: { xs: 'column', sm: 'row' },
                        gap: 1,
                        minWidth: 0,
                      }}
                    >
                      <Checkbox
                          checked={selectedProductIds.has(Number(product.id))}
                          onChange={() => toggleProductSelection(product.id)}
                          inputProps={{ 'aria-label': `Seleziona ${product.name}` }}
                          sx={{ mt: { xs: 0.5, sm: 1 } }}
                        />
                      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: { xs: 1.25, sm: 2 }, minWidth: 0, flex: '1 1 auto' }}>
                        <ListItemAvatar>
                          <Avatar
                            variant="rounded"
                            src={imgSrc}
                            alt={product.name}
                            sx={{ width: { xs: 50, sm: 56 }, height: { xs: 50, sm: 56 } }}
                          >
                            {product.name?.[0]?.toUpperCase() || '?'}
                          </Avatar>
                        </ListItemAvatar>

                        <ListItemText
                          primary={
                            <Box component="span" sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                              <span>{product.name}</span>
                              {product.active === false && (
                                <Chip size="small" color="error" variant="outlined" label="Disattivato" />
                              )}
                            </Box>
                          }
                          sx={{
                            minWidth: 0,
                            my: 0,
                            '& .MuiListItemText-primary, & .MuiListItemText-secondary': {
                              overflowWrap: 'anywhere',
                            },
                          }}
                          secondary={
                            <>
                              {getProductCategoryLabels(product).map((label) => (
                                <Typography key={label} component="span" display="block">
                                  {label}
                                </Typography>
                              ))}
                              {productRoomLabel && (
                                <Typography component="span" display="block">
                                  {`${t('product_room')}: ${productRoomLabel}`}
                                </Typography>
                              )}
                              {String(localStorage.getItem('tenantType') || '').toUpperCase() !== 'SALE_CONGRESSI' && product.boardBasis && (
                                <Typography component="span" display="block">
                                  {`${t('boardBasis.label', { defaultValue: 'Board basis' })}: ${formatBoardBasis(product.boardBasis)}`}
                                </Typography>
                              )}
                              <Typography component="span" display="block">
                                {`${t('price')}: ${parseFloat(product.price).toFixed(2)} ${product.pricePerWeight === true ? `€/${handleWeight(product.typeOfWeight)}` : '€'}`}
                              </Typography>
                            </>
                          }
                        />
                      </Box>

                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: { xs: 'flex-end', sm: 'flex-start' },
                          gap: { xs: 0.25, sm: 0.5 },
                          flexShrink: 0,
                          flexWrap: 'wrap',
                          width: { xs: '100%', sm: 'auto' },
                        }}
                      >
                        <Button onClick={() => { setOpenVariantList(true); setProdottoSelezionato(product); }}>
                          {t('variants')}
                        </Button>
                        <IconButton size="small" onClick={() => handleApriModifica(product)} aria-label={t('edit_product')}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                        <IconButton size="small" onClick={() => handleApriDuplica(product)} aria-label={t('duplicate')} color="primary">
                          <ContentCopyIcon fontSize="small" />
                        </IconButton>
                        <IconButton size="small" onClick={() => handleElimina(product.id)} aria-label={t('delete_button')}>
                          <DeleteIcon />
                        </IconButton>
                        <IconButton onClick={() => addTimeSlot(product.id)} color="primary" size="small" aria-label={t('addTimeSlot')}>
                          <AddIcon />
                        </IconButton>
                      </Box>
                    </Box>

                    {product.timeSlots.map((slot, index) => (
                      <Box
                        key={index}
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1,
                          width: '100%',
                          pl: { xs: 0, sm: '72px' },
                          flexWrap: 'wrap',
                        }}
                      >
                        <Box sx={{ flex: { xs: '1 1 calc(50% - 24px)', sm: '0 1 160px' }, minWidth: { xs: 0, sm: 150 } }}>
                          <TimePicker24Field
                            label={t('fromTime')}
                            value={slot.from}
                            onChange={(newValue) => handleTimeChange(product.id, index, 'from', newValue)}
                            ampm={false}
                            format="HH:mm"
                            size="small"
                          />
                        </Box>
                        <Box sx={{ flex: { xs: '1 1 calc(50% - 24px)', sm: '0 1 160px' }, minWidth: { xs: 0, sm: 150 } }}>
                          <TimePicker24Field
                            label={t('toTime')}
                            value={slot.to}
                            onChange={(newValue) => handleTimeChange(product.id, index, 'to', newValue)}
                            ampm={false}
                            format="HH:mm"
                            size="small"
                          />
                        </Box>
                        <IconButton
                          edge="end"
                          color="error"
                          size="small"
                          aria-label={t('removeTimeSlot')}
                          onClick={() => removeTimeSlot(product.id, index)}
                          disabled={product.timeSlots.length === 1}
                        >
                          <RemoveIcon />
                        </IconButton>
                      </Box>
                    ))}
                  </ListItem>
                );
              })}
            </List>
          )}
        </Paper>

        <Dialog
          open={bulkPrinterDialogOpen}
          onClose={() => {
            if (!bulkAssigningPrinters) setBulkPrinterDialogOpen(false);
          }}
          fullWidth
          maxWidth="sm"
        >
          <DialogTitle>{t('bulkPrinters.title')}</DialogTitle>
          <DialogContent>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
              <Typography variant="body2" color="text.secondary">
                {t('bulkPrinters.productsSelected', { count: selectedProductIds.size })}
              </Typography>
              {configuredPrinters.length === 0 ? (
                <Alert severity="warning">
                  {t('bulkPrinters.noPrintersConfigured')}
                </Alert>
              ) : (
                <FormControl fullWidth>
                  <InputLabel id="bulk-printer-targets-label">{t('bulkPrinters.printers')}</InputLabel>
                  <Select
                    labelId="bulk-printer-targets-label"
                    multiple
                    value={bulkPrinterTargets}
                    label={t('bulkPrinters.printers')}
                    onChange={(event) => {
                      const value = event.target.value;
                      setBulkPrinterTargets(typeof value === 'string' ? value.split(',') : value);
                    }}
                    renderValue={(selected) => selected.join(', ')}
                  >
                    {configuredPrinters.map((printer) => (
                      <MenuItem key={printer} value={printer}>
                        <Checkbox checked={bulkPrinterTargets.includes(printer)} />
                        <ListItemText primary={printer} />
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
            </Box>
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => setBulkPrinterDialogOpen(false)}
              disabled={bulkAssigningPrinters}
            >
              {t('cancel_button')}
            </Button>
            <Button
              variant="contained"
              onClick={handleBulkAssignPrinters}
              disabled={bulkAssigningPrinters || configuredPrinters.length === 0 || bulkPrinterTargets.length === 0}
              startIcon={bulkAssigningPrinters ? <CircularProgress size={16} /> : <PrintIcon />}
            >
              {bulkAssigningPrinters ? t('bulkPrinters.saving') : t('bulkPrinters.assign')}
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog
          open={bulkDeleteDialogOpen}
          onClose={() => {
            if (!bulkDeletingProducts) setBulkDeleteDialogOpen(false);
          }}
          fullWidth
          maxWidth="xs"
        >
          <DialogTitle>{t('bulkDelete.productsTitle')}</DialogTitle>
          <DialogContent>
            <Alert severity="warning" sx={{ mt: 1 }}>
              {t('bulkDelete.productsConfirm', { count: selectedProductIds.size })}
            </Alert>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setBulkDeleteDialogOpen(false)} disabled={bulkDeletingProducts}>
              {t('cancel_button')}
            </Button>
            <Button
              variant="contained"
              color="error"
              onClick={handleBulkDeleteProducts}
              disabled={bulkDeletingProducts}
              startIcon={bulkDeletingProducts ? <CircularProgress size={16} /> : <DeleteIcon />}
            >
              {bulkDeletingProducts ? t('bulkDelete.deleting') : t('bulkDelete.confirm')}
            </Button>
          </DialogActions>
        </Dialog>

        <ModalProdotto
          open={openModal}
          onClose={() => setOpenModal(false)}
          mode={modalMode}
          product={selectedProduct}
        />
        {openVariantList && (
          <ModalVariantList
            open={openVariantList}
            onClose={() => setOpenVariantList(false)}
            product={prodottoSelezionato}
          />
        )}
      </Container>
    </SidebarLayout>
  );
}

export default ListaProdotti;
